/**
 * MobileStrategyList Component
 *
 * Mobile page for displaying the list of strategies.
 * Route: /settings/strategies (mobile only)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Plus } from 'lucide-react';

import { Button, EmptyState, Alert, Card, CardHeader, CardBody } from '@enfomentis/ui';
import { useStrategiesPlaybooks } from '../context';
import { StrategyListItem } from '../components';

export const MobileStrategyList: React.FC = () => {
  const navigate = useNavigate();

  const {
    strategies,
    filteredStrategies,
    loading,
    loadError,
    showArchived,
    setShowArchived,
    reloadStrategies,
  } = useStrategiesPlaybooks();

  const handleStrategySelect = (strategyId: string) => {
    navigate(`/settings/strategies/${strategyId}`);
  };

  const handleShowArchivedToggle = () => {
    setShowArchived(!showArchived);
  };

  const handleNewStrategy = () => {
    navigate('/settings/strategies/new');
  };

  const hasStrategies = strategies.length > 0;
  const hasFilteredStrategies = filteredStrategies.length > 0;

  return (
    <div className="flex flex-col h-full gap-3">
      {/* Reference Data Card */}
      <Card radius="none" shadow="none" className="border-1 border-default-200">
        <CardBody className="py-1 px-3">
          <div className="flex items-center justify-between">
            <Button
              size="sm"
              variant="light"
              onPress={() => navigate('/settings/strategies/reference-data?tab=criteria')}
            >
              Criteria
            </Button>
            <Button
              size="sm"
              variant="light"
              onPress={() => navigate('/settings/strategies/reference-data?tab=confluences')}
            >
              Confluences
            </Button>
            <Button
              size="sm"
              variant="light"
              onPress={() => navigate('/settings/strategies/reference-data?tab=entry-models')}
            >
              Entry Models
            </Button>
            <Button
              size="sm"
              variant="light"
              onPress={() => navigate('/settings/strategies/reference-data?tab=categories')}
            >
              Categories
            </Button>
          </div>
        </CardBody>
      </Card>
      {/* Main Strategy List Card */}
      <Card radius="none" shadow="none" className="flex-1 min-h-0 flex flex-col">
        {/* Header */}
        <CardHeader className="flex items-center justify-between pb-1 border-b border-default-200">
          {/* Left: Archive toggle */}
          <Button
            size="sm"
            variant="light"
            startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
            onPress={handleShowArchivedToggle}
            className={showArchived ? 'text-text-primary' : 'text-text-tertiary'}
          >
            Archived
          </Button>

          {/* Right: New button */}
          <Button
            size="sm"
            variant="light"
            startContent={<Plus size={16} />}
            onPress={handleNewStrategy}
          >
            New
          </Button>
        </CardHeader>

        {/* Content */}
        <CardBody className="flex-1 min-h-0 overflow-y-auto px-1 pb-2">
          {/* Loading state */}
          {loading && (
            <div className="text-center py-8">
              <p className="text-text-secondary">Loading strategies...</p>
            </div>
          )}

          {/* Load error */}
          {loadError && !loading && (
            <div className="space-y-3">
              <Alert color="danger" variant="flat">
                Failed to load strategies. Please try again.
              </Alert>
              <Button size="sm" variant="flat" onPress={reloadStrategies}>
                Retry
              </Button>
            </div>
          )}

          {/* Empty state - No strategies */}
          {!hasStrategies && !loading && !loadError && (
            <EmptyState
              title="No strategies yet"
              description="Create your first strategy to get started."
              action={
                <Button
                  variant="flat"
                  startContent={<Plus size={16} />}
                  onPress={handleNewStrategy}
                >
                  New Strategy
                </Button>
              }
            />
          )}

          {/* Empty state - All archived */}
          {hasStrategies && !hasFilteredStrategies && !loading && !loadError && (
            <EmptyState
              title="No active strategies"
              description="All strategies are archived"
              action={
                <Button
                  variant="flat"
                  startContent={<Eye size={16} />}
                  onPress={handleShowArchivedToggle}
                >
                  Show Archived
                </Button>
              }
            />
          )}

          {/* Strategy list */}
          {!loading && !loadError && hasFilteredStrategies && (
            <div className="space-y-2">
              {filteredStrategies.map((strategy) => (
                <Card
                  key={strategy.id}
                  radius="lg"
                  shadow="none"
                  className="border border-default-200 overflow-hidden"
                >
                  <CardBody className="p-0">
                    <StrategyListItem
                      strategy={strategy}
                      isSelected={false}
                      isArchived={strategy.status === 'archived'}
                      onSelect={() => handleStrategySelect(strategy.id)}
                    />
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

    </div>
  );
};
